/**
 * Snapshot operativo del portal admin para el Dashboard.
 * Reutiliza RPCs ya usadas en Moderación / Clubes / Eventos / Tienda (pedidos).
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';
import { useAdminFishingClubsStats } from '@/pages/clubs/hooks/useFishingClubsAdmin';
import { useFishingEventsStats } from '@/pages/events/hooks/useFishingEventsAdmin';
import { useStoreOrdersStats } from '@/pages/store/orders/hooks/useStoreOrders';

export interface AdminOpsSnapshot {
    moderationPending: number;
    clubsPending: number;
    clubsReportsOpen: number;
    eventsPending: number;
    /** Pedidos físicos pendientes de envío (paid + processing). No materiales legacy. */
    storePending: number;
    isLoading: boolean;
}

export function useAdminOpsSnapshot(): AdminOpsSnapshot {
    // Misma shape/key que ModerationPage para no contaminar la cache de React Query
    const moderation = useQuery({
        queryKey: ['admin', 'moderation', 'stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_moderation_stats');
            if (error) throw error;
            return data as { pending_count?: number };
        },
        staleTime: 30_000,
    });

    const clubs = useAdminFishingClubsStats();
    const events = useFishingEventsStats();
    const storeOrders = useStoreOrdersStats();

    return {
        moderationPending: Number(moderation.data?.pending_count ?? 0),
        clubsPending: clubs.data?.pending_count ?? 0,
        clubsReportsOpen: clubs.data?.reports_open ?? 0,
        eventsPending: events.data?.pending_count ?? 0,
        storePending: storeOrders.data?.pendingShipment ?? 0,
        isLoading:
            moderation.isLoading ||
            clubs.isLoading ||
            events.isLoading ||
            storeOrders.isLoading,
    };
}
